import React, { memo, useContext } from "react";
import { ObjectContext } from "@providers/ObjectProvider";
import { useAuth } from "@providers/AuthProvider";
import { supportedRelationships } from "@api/schema/supportedRelationships";
import ObjectRelationshipCard from "@components/objects/ObjectRelationshipCard";
import ObjectRelationshipTable from "@components/objects/ObjectRelationshipTable";
import ObjectRelationshipNotes from "./ObjectRelationshipNotes";
import ObjectRelationshipUserTasks from "./ObjectRelationshipUserTasks";
import ObjectRelationshipInvolvements from "./ObjectRelationshipInvolvements";
import ObjectRelationshipFiles from "./ObjectRelationshipFiles";
import ObjectRelationshipTasks from "./ObjectRelationshipTasks";
import ObjectRelationshipHours from "./ObjectRelationshipHours";

export const ObjectRelationship = memo(function ObjectRelationship(
    { type, permissions = null, mutator = null, column = null, ...restProps }
) {
    const data = useContext(ObjectContext) ?? {};

    const { can } = useAuth();

    if(permissions !== null && !can(permissions)) {
        return null;
    }

    const rawRelationship = data[column ?? type];
    const relationship = typeof mutator === 'function' ? mutator(data) : rawRelationship;

    const displayType = supportedRelationships[type];

    if(!displayType) {
        return <em>not supported: {type}</em>
    }

    // Standard relationships
    if(displayType.type === 'table') {
        return (
            <ObjectRelationshipTable
                {...displayType}
                relationship={relationship}
                data={data}
                action={restProps?.action}
                callback={restProps?.callback}
                sx={restProps?.sx}
            />
        );
    }

    if(displayType.type === 'involvements') {
        return (
            <ObjectRelationshipInvolvements
                {...displayType}
                relationship={relationship}
            />
        );
    }

    if(displayType.type === 'files') {
        return (
            <ObjectRelationshipFiles
                {...displayType}
                relationship={relationship}
            />
        );
    }

    if(displayType.type === 'notes') {
        return (
            <ObjectRelationshipNotes
                {...displayType}
                relationship={relationship}
                {...restProps}
            />
        )
    }

    if(displayType.type === 'workplan_tasks') {
        return (
            <ObjectRelationshipUserTasks
                {...displayType}
                relationship={relationship}
            />
        )
    }

    if(displayType.type === 'tasks') {
        return (
            <ObjectRelationshipTasks
                {...displayType}
                relationship={relationship}
            />
        )
    }

    if(displayType.type === 'hours') {
        return (
            <ObjectRelationshipHours
                {...displayType}
                relationship={relationship}
            />
        )
    }

    return <em>not yet implemented {type}</em>
});
